import { Component, DestroyRef, Inject, OnDestroy, OnInit, Optional, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../../../../../environments/environment';

import { LocationSettingsFormFactory } from '../../models/location-settings-form.factory';
import { NotificationService } from '../../../../shared/services/notification.service';
import { LocationsFacade, CatalogsOptions } from '../../services/locations.facade';
import { LocationsService } from '../../services/locations.service';
import { MasterModel } from '../../../../master/models/master.model';
import { MerchantLocationGeneralDto } from '../../models/merchant-location-general.dto';

export type LocationFormSection =
  | 'general'
  | 'media'
  | 'concepts'
  | 'staff-concepts'
  | 'availability'
  | 'blocks'
  | 'booking-settings'
  | 'review';

export interface NavigationItem {
  id: LocationFormSection;
  label: string;
  icon: string;
  completed?: boolean;
  hasErrors?: boolean;
}

@Component({
    selector: 'app-location-settings-form',
    templateUrl: './location-settings-form.component.html',
    styleUrls: ['./location-settings-form.component.css'],
    standalone: false
})
export class LocationSettingsFormComponent implements OnInit, OnDestroy {
  mode: 'page' | 'modal' | 'panel' = 'page';

  form: UntypedFormGroup;
  currentSection: LocationFormSection = 'general';
  isLoading = true;
  isSaving = false;
  isEditMode = false;
  locationId: string | null = null;
  publicUrl: string | null = null;

  navigationItems: NavigationItem[] = [
    { id: 'general', label: 'Datos Generales', icon: 'business' },
    { id: 'media', label: 'Imagen y Apariencia', icon: 'image' },
    { id: 'concepts', label: 'Conceptos', icon: 'list_alt' },
    { id: 'staff-concepts', label: 'Empleados por Concepto', icon: 'people' },
    { id: 'availability', label: 'Disponibilidad', icon: 'schedule' },
    { id: 'blocks', label: 'Excepciones y Bloqueos', icon: 'block' },
    { id: 'booking-settings', label: 'Configuración de Reserva', icon: 'settings' },
    { id: 'review', label: 'Resumen', icon: 'check_circle' },
  ];

  actions: any[] = [];

  merchants: any[] = [];
  staff: any[] = [];

  isLoadingCatalogs = true;
  isSavingGeneral = false;

  countriesOptions: MasterModel[] = [];
  provincesOptions: MasterModel[] = [];
  municipalitiesOptions: MasterModel[] = [];
  locationStatusesOptions: MasterModel[] = [];
  merchantLocationCategoriesOptions: MasterModel[] = [];
  timeZonesOptions: MasterModel[] = [];
  currencyCodesOptions: MasterModel[] = [];

  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  protected readonly snackBar = inject(MatSnackBar);
  protected readonly dialog = inject(MatDialog);
  private readonly notify = inject(NotificationService);
  private readonly locationsFacade = inject(LocationsFacade);
  private readonly locationsService = inject(LocationsService);
  public readonly data = inject(MAT_DIALOG_DATA, { optional: true });
  public readonly dialogRef = inject(MatDialogRef<LocationSettingsFormComponent>, {
    optional: true,
  });

  constructor() {
    if (this.data && this.data.mode) {
      this.mode = this.data.mode;
    }

    this.form = LocationSettingsFormFactory.buildForm(this.fb);
  }

  async ngOnInit(): Promise<void> {
    await this.loadCatalogs();
    await this.loadLookups();
    await this.loadData();

    this.form.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateNavigationStatus();
      });

    this.isLoading = false;
  }

  ngOnDestroy(): void {}

  navigateTo(section: LocationFormSection): void {
    this.currentSection = section;
  }

  goToNextSection(): void {
    const currentIndex = this.navigationItems.findIndex((item) => item.id === this.currentSection);
    if (currentIndex < this.navigationItems.length - 1) {
      this.currentSection = this.navigationItems[currentIndex + 1].id;
    }
  }

  goToPreviousSection(): void {
    const currentIndex = this.navigationItems.findIndex((item) => item.id === this.currentSection);
    if (currentIndex > 0) {
      this.currentSection = this.navigationItems[currentIndex - 1].id;
    }
  }

  isFirstSection(): boolean {
    return this.currentSection === this.navigationItems[0].id;
  }

  isLastSection(): boolean {
    return this.currentSection === this.navigationItems[this.navigationItems.length - 1].id;
  }

  private updateNavigationStatus(): void {
    this.navigationItems.forEach((item) => {
      const sectionForm = this.getSectionFormGroup(item.id);
      if (sectionForm) {
        item.hasErrors = sectionForm.invalid && sectionForm.touched;
        item.completed = sectionForm.valid && sectionForm.touched;
      }
    });
  }

  private getSectionFormGroup(section: LocationFormSection): UntypedFormGroup | null {
    switch (section) {
      case 'general':
        return this.form.get('general') as UntypedFormGroup;
      case 'media':
        return this.form.get('media') as UntypedFormGroup;
      case 'availability':
        return this.form.get('availability') as UntypedFormGroup;
      case 'blocks':
        return this.form.get('blocks') as UntypedFormGroup;
      case 'booking-settings':
        return this.form.get('bookingSettings') as UntypedFormGroup;
      default:
        return null;
    }
  }

  private async loadCatalogs(): Promise<void> {
    this.isLoadingCatalogs = true;


    try {
      const catalogs = await firstValueFrom(this.locationsFacade.loadGeneralFormCatalogs());

      this.countriesOptions = catalogs.countries;
      this.provincesOptions = catalogs.provinces;
      this.locationStatusesOptions = catalogs.locationStatuses;
      this.timeZonesOptions = catalogs.timeZones;
      this.currencyCodesOptions = catalogs.currencyCodes;

      this.isLoadingCatalogs = false;

      if (!this.isEditMode) {
        this.applyDefaultValues();
      }
    } catch (error) {
      console.error('Error cargando catálogos:', error);
      this.notify.error('Error cargando catálogos');
      this.isLoadingCatalogs = false;
      throw error;
    }
  }

  private applyDefaultValues(): void {
    const generalControl = this.form.get('general');
    if (generalControl) {
      const currentValues = generalControl.value;

      const catalogs: CatalogsOptions = {
        countries: this.countriesOptions,
        provinces: this.provincesOptions,
        locationStatuses: this.locationStatusesOptions,
        locationCategories: this.merchantLocationCategoriesOptions,
        timeZones: this.timeZonesOptions,
        currencyCodes: this.currencyCodesOptions,
      };

      const defaultedValues = this.locationsFacade.applyDefaultValues(currentValues, catalogs);
      generalControl.patchValue(defaultedValues, { emitEvent: false });
    }
  }

  private async loadLookups(): Promise<void> {
    try {
      this.merchants = [];
      this.staff = [];
    } catch (error) {
      console.error('Error cargando lookups:', error);
      this.notify.error('Error cargando datos auxiliares');
    }
  }

  private async loadData(): Promise<void> {
    const params = await firstValueFrom(this.route.params);
    this.locationId = params['id'];
    this.isEditMode = !!this.locationId;

    if (this.isEditMode && this.locationId) {
      try {
        await this.loadLocationData(this.locationId);
      } catch (error) {
        console.error('Error cargando centro de trabajo:', error);
        this.notify.error('Error cargando los datos del centro de trabajo');
      }
    }
  }

  private applyDataToForm(data: any): void {
    if (!data) {
      return;
    }

    if (data.general) {
      const generalControl = this.form.get('general');
      if (generalControl) {
        generalControl.patchValue(data.general);
      }
    }

    if (data.media) {
      const mediaControl = this.form.get('media');
      if (mediaControl) {
        mediaControl.patchValue({
          logoUrl: data.media.logoUrl,
          bannerUrl: data.media.bannerUrl,
          primaryColor: data.media.primaryColor,
          secondaryColor: data.media.secondaryColor,
          accentColor: data.media.accentColor,
        });
      }

      if (data.media.images) {
        const imagesArray = LocationSettingsFormFactory.images(this.form);
        while (imagesArray.length !== 0) {
          imagesArray.removeAt(0);
        }
        data.media.images.forEach((img: any) => {
          const imgGroup = LocationSettingsFormFactory.createImageFormGroup(this.fb);
          imgGroup.patchValue(img);
          imagesArray.push(imgGroup);
        });
      }
    }

    if (data.concepts) {
      const conceptsArray = LocationSettingsFormFactory.serviceConcepts(this.form);
      while (conceptsArray.length !== 0) {
        conceptsArray.removeAt(0);
      }
      data.concepts.forEach((concept: any) => {
        const conceptGroup = LocationSettingsFormFactory.createConceptFormGroup(this.fb);
        conceptGroup.patchValue(concept);
        conceptsArray.push(conceptGroup);
      });
    }

    if (data.staffConcepts) {
      const staffConceptsArray = LocationSettingsFormFactory.staffConcepts(this.form);
      while (staffConceptsArray.length !== 0) {
        staffConceptsArray.removeAt(0);
      }
      data.staffConcepts.forEach((sc: any) => {
        const scGroup = LocationSettingsFormFactory.createStaffConceptFormGroup(this.fb);
        scGroup.patchValue(sc);
        staffConceptsArray.push(scGroup);
      });
    }

    if (data.availability) {
      const availabilityControl = this.form.get('availability');
      if (availabilityControl) {
        availabilityControl.patchValue({
          defaultSlotDuration: data.availability.defaultSlotDuration,
          slotStep: data.availability.slotStep,
          minAdvanceBooking: data.availability.minAdvanceBooking,
          maxAdvanceBooking: data.availability.maxAdvanceBooking,
        });
      }

      if (data.availability.weeklyRules) {
        const weeklyRulesArray = LocationSettingsFormFactory.weeklyRules(this.form);
        while (weeklyRulesArray.length !== 0) {
          weeklyRulesArray.removeAt(0);
        }
        data.availability.weeklyRules.forEach((rule: any) => {
          const ruleGroup = LocationSettingsFormFactory.createWeeklyRuleFormGroup(this.fb);
          ruleGroup.patchValue(rule);
          weeklyRulesArray.push(ruleGroup);
        });
      }
    }

    if (data.blocks) {
      if (data.blocks.exceptions) {
        const exceptionsArray = LocationSettingsFormFactory.exceptions(this.form);
        while (exceptionsArray.length !== 0) {
          exceptionsArray.removeAt(0);
        }
        data.blocks.exceptions.forEach((exc: any) => {
          const excGroup = LocationSettingsFormFactory.createExceptionFormGroup(this.fb);
          excGroup.patchValue(exc);
          exceptionsArray.push(excGroup);
        });
      }

      if (data.blocks.manualBlocks) {
        const blocksArray = LocationSettingsFormFactory.manualBlocks(this.form);
        while (blocksArray.length !== 0) {
          blocksArray.removeAt(0);
        }
        data.blocks.manualBlocks.forEach((block: any) => {
          const blockGroup = LocationSettingsFormFactory.createManualBlockFormGroup(this.fb);
          blockGroup.patchValue(block);
          blocksArray.push(blockGroup);
        });
      }
    }
  }

  async onSaveGeneralSection(formData: MerchantLocationGeneralDto): Promise<void> {
    this.isSavingGeneral = true;

    try {
      if (this.isEditMode && this.locationId) {
        await firstValueFrom(this.locationsFacade.updateLocationGeneral(this.locationId, formData));
        this.notify.ok('Datos generales actualizados correctamente');

        await this.loadLocationData(this.locationId);
      } else {
        const result: any = await firstValueFrom(this.locationsFacade.createLocation(formData));

        if (result && result.value && result.value.merchantLocationId) {
          this.locationId = result.value.merchantLocationId;
          this.isEditMode = true;
          this.notify.ok(
            'Centro de trabajo creado correctamente. Ahora puedes configurar el resto de opciones.'
          );

          await this.loadLocationData(this.locationId);
        } else {
          this.locationId = 'temp-' + Date.now();
          this.isEditMode = true;
          this.notify.ok('Centro de trabajo creado correctamente.');
        }
      }
    } catch (error) {
      console.error('Error guardando datos generales:', error);
      this.notify.error('Error al guardar los datos generales');
    } finally {
      this.isSavingGeneral = false;
    }
  }

  private async loadLocationData(locationId: string): Promise<void> {
    try {
      const locationData = await firstValueFrom(this.locationsFacade.loadLocationById(locationId));

      if (locationData) {

        const countryObj = this.countriesOptions.find((c) => c.id === locationData.countryCode);
        const timeZoneObj = this.timeZonesOptions.find((tz) => tz.id === locationData.timeZoneId);
        const currencyObj = this.currencyCodesOptions.find(
          (cc) => cc.id === locationData.currencyCode
        );
        const statusObj = locationData.statusMerchantLocationId
          ? this.locationStatusesOptions.find(
              (s) => s.id === locationData.statusMerchantLocationId.toString()
            )
          : null;

        let stateObj = null;
        let cityObj = null;

        if (locationData.state) {
          stateObj = this.provincesOptions.find((p) => p.display === locationData.state);

          if (stateObj && stateObj.id) {
            try {
              const municipalities = await firstValueFrom(
                this.locationsFacade.loadMunicipalities(stateObj.id)
              );
              this.municipalitiesOptions = municipalities;

              cityObj = municipalities.find((m) => m.display === locationData.city);
            } catch (error) {
              console.error('Error cargando municipios:', error);
            }
          }
        }

        this.buildPublicUrlFromList(locationId, locationData.slug);

        this.form.patchValue({
          general: {
            merchantLocationId: locationData.merchantLocationId,
            merchantId: locationData.merchantId,
            name: locationData.name,
            slug: locationData.slug,
            description: locationData.description,
            addressLine1: locationData.addressLine1,
            addressLine2: locationData.addressLine2,
            city: cityObj || locationData.city,
            state: stateObj || locationData.state,
            postalCode: locationData.postalCode,
            countryCode: countryObj || locationData.countryCode,
            latitude: locationData.latitude,
            longitude: locationData.longitude,
            timeZoneId: timeZoneObj || locationData.timeZoneId,
            currencyCode: currencyObj || locationData.currencyCode,
            phone: locationData.phone,
            email: locationData.email,
            isMainLocation: locationData.isMainLocation,
            displayOrder: locationData.displayOrder,
            statusMerchantLocationId: statusObj || locationData.statusMerchantLocationId,
            merchantLocationCategoryId: locationData.merchantLocationCategoryId,
            metadata: locationData.metadata,
            businessHours: locationData.businessHours,
          },
        });
      }
    } catch (error) {
      console.error('Error cargando datos del centro de trabajo:', error);
      this.notify.error('Error cargando los datos del centro de trabajo');
    }
  }

  onCountryChange(countryCode: string): void {
    this.municipalitiesOptions = [];
  }

  onStateChange(stateCode: string): void {
    if (stateCode) {
      this.locationsFacade
        .loadMunicipalities(stateCode)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (municipalities) => {
            this.municipalitiesOptions = municipalities;
          },
          error: (error) => {
            console.error('Error cargando municipios:', error);
            this.notify.error('Error cargando municipios');
          },
        });
    } else {
      this.municipalitiesOptions = [];
    }
  }

  async onSave(): Promise<void> {
    console.warn('onSave legacy llamado - ya no debería usarse');
  }

  private buildPublicUrlFromList(locationId: string, slug: string): void {
    this.locationsService
      .getList({ page: 1, pageSize: 500 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: any) => {
        if (!res || !res.values) {
          return;
        }
        const match = res.values.find(
          (l: any) =>
            (l.merchantLocationId && l.merchantLocationId.toString() === locationId) ||
            l.uid === locationId
        );
        if (match && match.merchantSlug && slug) {
          this.publicUrl = `${environment.bookingUrl}/${match.merchantSlug}/${slug}`;
        }
      });
  }

  openPublicUrl(): void {
    if (this.publicUrl) {
      window.open(this.publicUrl, '_blank');
    }
  }

  onCancel(): void {
    if (this.mode === 'modal' && this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.router.navigate(['/booking/locations']);
    }
  }

  onTabChange(event: any): void {
    if (event.index !== 0 && !this.locationId) {
      setTimeout(() => {
        event.source.selectedIndex = 0;
      }, 0);

      this.notify.warning(
        'Guarda primero el nuevo centro de trabajo para poder seguir configurándolo.'
      );
    }
  }

  private markFormGroupTouched(formGroup: UntypedFormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control) {
        control.markAsTouched();
      }

      if (control instanceof UntypedFormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  expand(): void {
    if (this.dialogRef) {
    }
  }

  getCurrentSectionLabel(): string {
    const item = this.navigationItems.find((item) => item.id === this.currentSection);
    return item ? item.label : '';
  }

  getCompletedSections(): number {
    return this.navigationItems.filter((item) => item.completed).length;
  }

  getProgressPercentage(): number {
    const completed = this.getCompletedSections();
    return (completed / this.navigationItems.length) * 100;
  }
}